<template>
  <div>
    <!-- Toolbar modalità -->
    <div class="d-flex align-center mb-2">
      <v-btn-toggle v-model="modalita" mandatory dense borderless>
        <v-btn value="lettura" small>
          <v-icon small class="mr-1">mdi-eye-outline</v-icon>
          Lettura
        </v-btn>
        <v-btn value="modifica" small :disabled="readonly">
          <v-icon small class="mr-1">mdi-pencil-outline</v-icon>
          Modifica
        </v-btn>
      </v-btn-toggle>
      <v-spacer/>
      <span class="caption grey--text">{{ appezzamento.nome }} · {{ W }}×{{ H }}m</span>
    </div>

    <v-row no-gutters>
      <!-- Canvas SVG -->
      <v-col cols="9">
        <div style="position:relative">
          <svg
            ref="svg_canvas"
            :viewBox="dynamicViewBox"
            :style="svgStyle"
            @mousemove="onMouseMove"
            @mouseup="onMouseUp"
            @mouseleave="onMouseLeave"
            @wheel="onWheel"
          >
            <defs>
              <pattern
                :id="`grid-1m-${appezzamento.id}`"
                x="0" y="0" width="1" height="1"
                patternUnits="userSpaceOnUse"
              >
                <path d="M 1 0 L 0 0 0 1" fill="none" stroke="#C8E6C9" stroke-width="0.04"/>
              </pattern>
              <pattern
                :id="`grid-5m-${appezzamento.id}`"
                x="0" y="0" width="5" height="5"
                patternUnits="userSpaceOnUse"
              >
                <rect width="5" height="5" :fill="`url(#grid-1m-${appezzamento.id})`"/>
                <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#A5D6A7" stroke-width="0.1"/>
              </pattern>
            </defs>

            <!-- Terreno + griglia (anche hit area per pan) -->
            <rect
              :width="W" :height="H"
              :fill="`url(#grid-5m-${appezzamento.id})`"
              :style="{ cursor: !isEditMode && zoom_scale > 1 ? 'grab' : 'default' }"
              @mousedown="startPan"
            />

            <!-- ===== BLOCCHI (sotto le serre) ===== -->
            <g
              v-for="blocco in blocchi"
              :key="'b-' + blocco.id"
              :transform="bloccoTransform(blocco)"
              @mousedown="startDragBlocco($event, blocco)"
            >
              <rect
                x="0" y="0"
                :width="blocco.larghezza || 10"
                :height="blocco.lunghezza || 5"
                :fill="blocco.colore || '#CFD8DC'"
                stroke="none"
                rx="0.15"
                pointer-events="none"
              />
              <text
                v-if="blocco.label"
                :x="(blocco.larghezza || 10) / 2"
                :y="(blocco.lunghezza || 5) / 2"
                text-anchor="middle"
                dominant-baseline="middle"
                :font-size="Math.min(1.0, Math.max(0.45, (blocco.larghezza || 10) / 12))"
                fill="#546E7A"
                pointer-events="none"
              >{{ blocco.label }}</text>

              <!-- Hit area (attiva solo in modifica) -->
              <rect
                x="0" y="0"
                :width="blocco.larghezza || 10"
                :height="blocco.lunghezza || 5"
                fill="transparent"
                :style="{ cursor: 'move', pointerEvents: isEditMode ? 'auto' : 'none' }"
              />

              <!-- Maniglia rotazione (solo in modifica) -->
              <g v-if="isEditMode" @mousedown.stop="startRotateBlocco($event, blocco)" style="cursor:crosshair">
                <line
                  :x1="(blocco.larghezza || 10) / 2"
                  y1="0"
                  :x2="(blocco.larghezza || 10) / 2"
                  y2="-1.8"
                  stroke="#90A4AE"
                  stroke-width="0.12"
                  stroke-dasharray="0.2 0.2"
                  pointer-events="none"
                />
                <circle
                  :cx="(blocco.larghezza || 10) / 2"
                  cy="-1.8"
                  r="0.65"
                  fill="#90A4AE"
                  stroke="white"
                  stroke-width="0.12"
                />
                <text
                  :x="(blocco.larghezza || 10) / 2"
                  y="-1.8"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="0.7"
                  fill="white"
                  pointer-events="none"
                >↻</text>
              </g>

              <!-- Tasto × rimozione (solo in modifica) -->
              <g v-if="isEditMode" @mousedown.stop @click.stop="$emit('blocco-rimosso', blocco.id)" style="cursor:pointer">
                <circle
                  :cx="(blocco.larghezza || 10) - 0.75"
                  cy="0.75"
                  r="0.62"
                  fill="#78909C"
                  stroke="white"
                  stroke-width="0.1"
                />
                <text
                  :x="(blocco.larghezza || 10) - 0.75"
                  y="0.75"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="0.8"
                  fill="white"
                  pointer-events="none"
                >×</text>
              </g>
            </g>

            <!-- ===== SERRE (sopra i blocchi) ===== -->
            <g
              v-for="pos in posizioni_con_serra"
              :key="pos.id"
              :transform="serraTransform(pos)"
              :opacity="!fork_mode && pos.serra.dismessa ? 0.45 : 1"
              @mousedown="startDrag($event, pos)"
              @mouseenter="!isEditMode && (hover_id_serra = pos.id_serra)"
              @mouseleave="hover_id_serra = null"
            >
              <rect
                x="0.11" y="0.11"
                :width="(pos.serra.larghezza || 10) - 0.22"
                :height="(pos.serra.lunghezza || 20) - 0.22"
                :fill="!fork_mode && pos.serra.dismessa ? '#ECEFF1' : serraFill(pos.serra.tipo)"
                :stroke="!fork_mode && pos.serra.dismessa ? '#90A4AE' : serraStroke(pos.serra.tipo)"
                stroke-width="0.22"
                rx="0.34"
                pointer-events="none"
              />
              <path
                :d="serraHeaderPath(pos.serra)"
                :fill="!fork_mode && pos.serra.dismessa ? '#90A4AE' : serraStroke(pos.serra.tipo)"
                fill-opacity="0.88"
                pointer-events="none"
              />
              <text
                :x="(pos.serra.larghezza || 10) / 2"
                :y="(0.11 + serraHeaderH(pos.serra)) / 2"
                text-anchor="middle"
                dominant-baseline="middle"
                font-weight="bold"
                :font-size="nomeFontSize(pos.serra)"
                fill="white"
                pointer-events="none"
              >{{ pos.serra.nome }}</text>
              <text
                :x="(pos.serra.larghezza || 10) / 2"
                :y="serraHeaderH(pos.serra) + ((pos.serra.lunghezza || 20) - 0.11 - serraHeaderH(pos.serra)) * 0.5"
                text-anchor="middle"
                dominant-baseline="middle"
                font-size="0.72"
                :fill="serraStroke(pos.serra.tipo)"
                opacity="0.7"
                pointer-events="none"
              >{{ pos.serra.larghezza || '?' }}×{{ pos.serra.lunghezza || '?' }}m</text>

              <!-- Hit area -->
              <rect
                x="0" y="0"
                :width="pos.serra.larghezza || 10"
                :height="pos.serra.lunghezza || 20"
                fill="transparent"
                :style="{ cursor: isEditMode ? 'move' : (!fork_mode && pos.serra.dismessa ? 'default' : 'pointer') }"
              />

              <!-- Maniglia rotazione (solo in modifica) -->
              <g v-if="isEditMode" @mousedown.stop="startRotate($event, pos)" style="cursor:crosshair">
                <line
                  :x1="(pos.serra.larghezza || 10) / 2"
                  y1="0"
                  :x2="(pos.serra.larghezza || 10) / 2"
                  y2="-1.8"
                  :stroke="serraStroke(pos.serra.tipo)"
                  stroke-width="0.12"
                  stroke-dasharray="0.2 0.2"
                  pointer-events="none"
                />
                <circle
                  :cx="(pos.serra.larghezza || 10) / 2"
                  cy="-1.8"
                  r="0.65"
                  :fill="serraStroke(pos.serra.tipo)"
                  stroke="white"
                  stroke-width="0.12"
                />
                <text
                  :x="(pos.serra.larghezza || 10) / 2"
                  y="-1.8"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="0.7"
                  fill="white"
                  pointer-events="none"
                >↻</text>
              </g>

              <!-- Tasto × rimozione (solo in modifica) -->
              <g v-if="isEditMode" @mousedown.stop @click.stop="$emit('posizione-rimossa', pos.id)" style="cursor:pointer">
                <circle
                  :cx="(pos.serra.larghezza || 10) - 0.75"
                  cy="0.75"
                  r="0.62"
                  fill="#E53935"
                  stroke="white"
                  stroke-width="0.1"
                />
                <text
                  :x="(pos.serra.larghezza || 10) - 0.75"
                  y="0.75"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="0.8"
                  fill="white"
                  pointer-events="none"
                >×</text>
              </g>
            </g>

            <!-- Indicatore scala 10m -->
            <template v-if="W >= 15">
              <line x1="1" :y1="H - 1.6" x2="11" :y2="H - 1.6" stroke="#7CB342" stroke-width="0.12"/>
              <line x1="1" :y1="H - 1.9" x2="1" :y2="H - 1.3" stroke="#7CB342" stroke-width="0.08"/>
              <line x1="11" :y1="H - 1.9" x2="11" :y2="H - 1.3" stroke="#7CB342" stroke-width="0.08"/>
              <text x="6" :y="H - 2.2" text-anchor="middle" font-size="0.85" fill="#558B2F">10m</text>
            </template>

            <!-- Stato vuoto -->
            <text
              v-if="!posizioni_con_serra.length && !blocchi.length"
              :x="W / 2"
              :y="H / 2"
              text-anchor="middle"
              dominant-baseline="middle"
              :font-size="Math.max(1.5, Math.min(3, W / 20))"
              fill="#B2DFDB"
              pointer-events="none"
            >Aggiungi serre dalla lista →</text>
          </svg>

          <!-- Overlay info durante drag/rotazione -->
          <div v-if="overlayInfo" class="coords-overlay">{{ overlayInfo }}</div>

          <!-- Zoom indicator + reset -->
          <div v-if="zoom_scale > 1" class="zoom-overlay">
            {{ Math.round(zoom_scale * 10) / 10 }}×
            <span class="zoom-reset" @click="resetZoom" title="Reimposta zoom">✕</span>
          </div>

          <!-- Tooltip hover recap prodotti -->
          <div
            v-if="!isEditMode && !dragging && hoverRecap"
            class="serra-tooltip"
            :style="{ left: hover_mouse.x + 'px', top: hover_mouse.y + 'px' }"
          >
            <div class="caption font-weight-bold grey--text text--darken-1 mb-1">
              {{ hoverSerraNome }}
            </div>
            <template v-if="hoverRecap.righe.length">
              <div
                v-for="item in hoverRecap.righe"
                :key="item.id"
                class="d-flex align-start tooltip-row"
              >
                <template v-if="!item.component_infos || item.component_infos.length <= 1">
                  <img v-if="item.img_url" :src="item.img_url" class="tooltip-swatch mr-2 flex-shrink-0" />
                  <div v-else class="tooltip-swatch mr-2 flex-shrink-0" :style="{ backgroundColor: item.color }" />
                </template>
                <svg v-else class="tooltip-swatch mr-2 flex-shrink-0" viewBox="0 0 20 20" width="20" height="20">
                  <defs>
                    <clipPath
                      v-for="(inf, idx) in item.component_infos"
                      :key="`cpac-${item.id}-${idx}`"
                      :id="`pac-${item.id}-${idx}`"
                    >
                      <path :d="_sectorPath(idx, item.component_infos.length, 10, 10, 10)"/>
                    </clipPath>
                  </defs>
                  <g v-for="(inf, idx) in item.component_infos" :key="`secac-${item.id}-${idx}`">
                    <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="20" height="20"
                           :clip-path="`url(#pac-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                    <path v-else :d="_sectorPath(idx, item.component_infos.length, 10, 10, 10)"
                          :fill="inf.fallback"/>
                  </g>
                  <circle cx="10" cy="10" r="9.5" fill="none" stroke="rgba(0,0,0,0.1)" stroke-width="1"/>
                </svg>
                <div class="flex-grow-1" style="line-height:1.3; min-width:0">
                  <div v-if="item.tipo_contenitore" class="text-caption grey--text text--darken-1" style="line-height:1;margin-bottom:1px">{{ item.tipo_contenitore }}</div>
                  <div v-if="item.specie_nomi" class="text-caption grey--text font-italic" style="line-height:1">{{ item.specie_nomi }}</div>
                  <div v-for="(riga, ri) in item.righe_dettaglio" :key="ri" class="text-caption tooltip-label">{{ riga }}</div>
                  <div v-if="!item.righe_dettaglio || !item.righe_dettaglio.length" class="text-caption tooltip-label">{{ item.label }}</div>
                </div>
                <div class="d-flex flex-column align-end ml-2 flex-shrink-0" style="white-space:nowrap">
                  <span class="text-caption grey--text">{{ item.diametro ? `Ø${item.diametro}` : '—' }}</span>
                  <span class="text-caption font-weight-medium">{{ item.vasi }}v</span>
                </div>
              </div>
              <div class="tooltip-footer caption grey--text mt-1">
                Liberi: {{ hoverRecap.liberi }} · Totale: {{ hoverRecap.totale_vasi }}
              </div>
            </template>
            <div v-else class="caption grey--text">Nessun prodotto assegnato</div>
          </div>
        </div>
      </v-col>

      <!-- Side panel -->
      <v-col cols="3" class="pl-4">
        <!-- Sezione modifica -->
        <template v-if="isEditMode">
          <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2 mt-1">
            SERRE NON POSIZIONATE
          </div>
          <v-alert v-if="!serre_non_posizionate.length" type="success" outlined dense class="mb-3" style="font-size:12px">
            Tutte le serre sono in mappa.
          </v-alert>
          <v-list v-else dense class="py-0 mb-3">
            <v-list-item v-for="s in serre_non_posizionate" :key="s.id" class="pl-2 mb-1">
              <v-list-item-avatar size="20" class="mr-2 my-0" style="min-width:20px">
                <v-icon small :color="serraStroke(s.tipo)">mdi-greenhouse</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ s.nome }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ s.larghezza || '?' }}×{{ s.lunghezza || '?' }}m</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn x-small color="primary" outlined @click="posizionaCentro(s)">
                  <v-icon x-small>mdi-plus</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-divider class="mb-3"/>

          <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">BLOCCHI</div>
          <p class="caption grey--text mb-2">
            Rettangoli grigi componibili per rappresentare edifici, muri, siepi e strutture non-serra.
          </p>
          <v-btn small outlined color="blue-grey" class="mb-3" @click="aggiungiBlocco">
            <v-icon small class="mr-1">mdi-plus</v-icon>Aggiungi blocco
          </v-btn>

          <v-divider class="mb-3"/>
        </template>

        <!-- Serre in mappa (sempre visibile) -->
        <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">SERRE IN MAPPA</div>
        <v-alert v-if="!posizioni_con_serra.length" type="info" outlined dense style="font-size:12px">
          Nessuna serra posizionata.
        </v-alert>
        <v-list v-else dense class="py-0">
          <v-list-item
            v-for="pos in posizioni_con_serra"
            :key="pos.id"
            class="pl-2 mb-1"
            :style="{ cursor: !fork_mode && pos.serra.dismessa ? 'default' : 'pointer', opacity: !fork_mode && pos.serra.dismessa ? 0.6 : 1 }"
            @click="(fork_mode || !pos.serra.dismessa) && $emit('serra-selezionata', pos.id_serra)"
          >
            <v-list-item-avatar size="20" class="mr-2 my-0" style="min-width:20px">
              <v-icon small :color="!fork_mode && pos.serra.dismessa ? 'grey' : serraStroke(pos.serra.tipo)">mdi-greenhouse</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2" :class="!fork_mode && pos.serra.dismessa ? 'grey--text' : ''">
                {{ pos.serra.nome }}
                <v-chip v-if="!fork_mode && pos.serra.dismessa" x-small label color="grey" dark class="ml-1">Dismessa</v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                <template v-if="completamento_serre[pos.id_serra] != null">
                  <span class="grey--text">
                    {{ file_per_serra[pos.id_serra] != null ? file_per_serra[pos.id_serra] + ' file · ' : '' }}{{ (recap_per_serra[pos.id_serra] || {}).totale_vasi || 0 }} vasi ·
                  </span>
                  <span :style="{ color: completamento_serre[pos.id_serra] >= 80 ? '#2E7D32' : completamento_serre[pos.id_serra] >= 40 ? '#E65100' : 'inherit' }">
                    {{ completamento_serre[pos.id_serra] }}%
                  </span>
                </template>
                <span v-else class="grey--text">
                  {{ pos.serra.larghezza || '?' }}×{{ pos.serra.lunghezza || '?' }}m<template v-if="pos.serra.posizione != null"> · #{{ pos.serra.posizione }}</template>
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center ma-0" style="gap:0">
              <v-btn
                v-if="isEditMode"
                icon x-small color="red"
                title="Rimuovi dal canvas"
                @click.stop="$emit('posizione-rimossa', pos.id)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
              <v-icon v-if="(fork_mode || !pos.serra.dismessa) && !isEditMode" small color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>

    <!-- Dialog modifica blocco -->
    <v-dialog v-model="dialog_blocco" max-width="360">
      <v-card>
        <v-card-title class="text-subtitle-1 pb-1">
          <v-icon class="mr-2" color="blue-grey">mdi-square-outline</v-icon>
          Modifica blocco
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="blocco_editing.label"
            label="Etichetta (opzionale)"
            outlined dense hide-details
            class="mb-3"
          />
          <div class="caption grey--text mb-1">Colore</div>
          <div class="d-flex mb-4" style="gap:10px">
            <div
              v-for="c in bloccoColori"
              :key="c.valore"
              :title="c.nome"
              :style="{
                width: '32px', height: '32px', borderRadius: '6px',
                backgroundColor: c.valore, cursor: 'pointer',
                border: blocco_editing.colore === c.valore
                  ? '3px solid #455A64'
                  : '2px solid #B0BEC5',
                boxSizing: 'border-box',
              }"
              @click="blocco_editing.colore = c.valore"
            />
          </div>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model.number="blocco_editing.larghezza"
                label="Larghezza (m)"
                type="number" outlined dense hide-details :min="1"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="blocco_editing.lunghezza"
                label="Lunghezza (m)"
                type="number" outlined dense hide-details :min="1"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialog_blocco = false">Annulla</v-btn>
          <v-spacer/>
          <v-btn color="blue-grey" dark @click="salvaBlocco">Salva</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const BLOCCO_COLORI = [
  { valore: '#CFD8DC', nome: 'Grigio'       },
  { valore: '#B0BEC5', nome: 'Grigio scuro' },
  { valore: '#D7CCC8', nome: 'Tortora'      },
  { valore: '#BBDEFB', nome: 'Azzurro'      },
  { valore: '#FFE0B2', nome: 'Pesca'        },
];

const TIPI_SERRA = {
  ferrovetro:   { fill: '#E3F2FD', stroke: '#1565C0' },
  film:         { fill: '#FFF8E1', stroke: '#E65100' },
  ombreggiante: { fill: '#EDE7F6', stroke: '#4527A0' },
  campo_aperto: { fill: '#E8F5E9', stroke: '#2E7D32' },
};
const TIPO_FALLBACK = { fill: '#ECEFF1', stroke: '#546E7A' };

export default {
  name: "AppezzamentoCanvas",
  props: {
    appezzamento: { type: Object, required: true },
    posizioni:    { type: Array,  default: () => [] },
    serre:        { type: Array,  default: () => [] },
    blocchi:             { type: Array,   default: () => [] },
    completamento_serre: { type: Object,  default: () => ({}) },
    recap_per_serra:     { type: Object,  default: () => ({}) },
    file_per_serra:      { type: Object,  default: () => ({}) },
    readonly:            { type: Boolean, default: false },
    fork_mode:           { type: Boolean, default: false },
  },
  data() {
    return {
      modalita:          'lettura',  // 'lettura' | 'modifica'
      zoom_scale:        1,
      pan_cx:            0,
      pan_cy:            0,
      panning:           null,       // { start_cx, start_cy, origin_px, origin_py }
      temp_pos:          {},         // { [id_posizione]: { cx, cy } } — serre
      temp_rot:          {},         // { [id_posizione]: gradi } — serre
      temp_pos_b:        {},         // { [id_blocco]: { cx, cy } }
      temp_rot_b:        {},         // { [id_blocco]: gradi }
      dragging:          null,       // { _tipo, id, offset_cx, offset_cy, rot, origin_cx, origin_cy, _w, _h, [id_serra] }
      rotating:          null,       // { _tipo, id, cx, cy, start_angle, start_rot, [id_serra] }
      drag_start:        null,
      hover_id_serra:    null,
      hover_mouse:       { x: 0, y: 0 },
      dialog_blocco:     false,
      blocco_editing_id: null,
      blocco_editing:    { label: '', larghezza: 10, lunghezza: 5, colore: '#CFD8DC' },
    };
  },
  computed: {
    isEditMode() { return this.modalita === 'modifica' && !this.readonly; },
    bloccoColori() { return BLOCCO_COLORI; },
    W() { return this.appezzamento.larghezza || 100; },
    H() { return this.appezzamento.lunghezza  || 60; },
    dynamicViewBox() {
      const vw = this.W / this.zoom_scale;
      const vh = this.H / this.zoom_scale;
      const x  = Math.max(0, Math.min(this.W - vw, this.pan_cx - vw / 2));
      const y  = Math.max(0, Math.min(this.H - vh, this.pan_cy - vh / 2));
      return `${x} ${y} ${vw} ${vh}`;
    },
    serre_posizionate_ids() {
      return new Set(this.posizioni.map((p) => p.id_serra));
    },
    serre_non_posizionate() {
      return this.serre.filter((s) =>
        (this.fork_mode || !s.dismessa) && !this.serre_posizionate_ids.has(s.id)
      );
    },
    posizioni_con_serra() {
      return this.posizioni
        .map((p) => ({ ...p, serra: this.serre.find((s) => s.id === p.id_serra) }))
        .filter((p) => p.serra)
        .sort((a, b) => (a.serra.posizione ?? Infinity) - (b.serra.posizione ?? Infinity));
    },
    svgStyle() {
      let cursor = 'default';
      if (this.dragging) cursor = 'grabbing';
      else if (this.rotating) cursor = 'crosshair';
      else if (this.panning) cursor = 'grabbing';
      return {
        width: '100%', display: 'block',
        border: '1px solid #A5D6A7', borderRadius: '8px',
        background: '#F1F8E9', cursor, userSelect: 'none',
      };
    },
    hoverRecap() {
      if (!this.hover_id_serra) return null;
      return this.recap_per_serra[this.hover_id_serra] || null;
    },
    hoverSerraNome() {
      if (!this.hover_id_serra) return '';
      const pos = this.posizioni_con_serra.find((p) => p.id_serra === this.hover_id_serra);
      return pos?.serra?.nome || '';
    },
    overlayInfo() {
      if (this.dragging) {
        const t = (this.dragging._tipo === 'blocco' ? this.temp_pos_b : this.temp_pos)[this.dragging.id];
        return t ? `cx: ${t.cx}m · cy: ${t.cy}m` : null;
      }
      if (this.rotating) {
        const r = (this.rotating._tipo === 'blocco' ? this.temp_rot_b : this.temp_rot)[this.rotating.id];
        return r !== undefined ? `↻ ${r}°` : null;
      }
      return null;
    },
  },
  watch: {
    appezzamento: {
      immediate: true,
      handler() { this.$nextTick(() => this.resetZoom()); },
    },
    readonly(val) {
      if (val) this.modalita = 'lettura';
    },
  },
  methods: {
    /* ---- pie tooltip ---- */
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },

    /* ---- zoom / pan ---- */
    resetZoom() {
      this.zoom_scale = 1;
      this.pan_cx     = this.W / 2;
      this.pan_cy     = this.H / 2;
    },
    onWheel(event) {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const factor    = event.deltaY < 0 ? 1.2 : 1 / 1.2;
      const new_scale = Math.min(8, Math.max(1, this.zoom_scale * factor));
      if (new_scale === this.zoom_scale) return;
      const pt    = this.screenToSvg(event);
      this.pan_cx = pt.x + (this.pan_cx - pt.x) * this.zoom_scale / new_scale;
      this.pan_cy = pt.y + (this.pan_cy - pt.y) * this.zoom_scale / new_scale;
      this.zoom_scale = new_scale;
    },
    startPan(event) {
      if (this.isEditMode || this.zoom_scale <= 1) return;
      event.preventDefault();
      this.panning = {
        start_cx:  event.clientX,
        start_cy:  event.clientY,
        origin_px: this.pan_cx,
        origin_py: this.pan_cy,
      };
    },

    /* ---- colori serre per tipo struttura ---- */
    serraFill(tipo)   { return (TIPI_SERRA[tipo] || TIPO_FALLBACK).fill; },
    serraStroke(tipo) { return (TIPI_SERRA[tipo] || TIPO_FALLBACK).stroke; },

    /* ---- header band serre ---- */
    serraHeaderH(serra) {
      const w = serra.larghezza || 10;
      return Math.max(2.2, Math.min(4.5, w * 0.28));
    },
    serraHeaderPath(serra) {
      const w  = serra.larghezza || 10;
      const hH = this.serraHeaderH(serra);
      const o  = 0.11;
      const rx = 0.34;
      const r  = w - o;
      return `M ${o + rx} ${o} L ${r - rx} ${o} Q ${r} ${o} ${r} ${o + rx} L ${r} ${hH} L ${o} ${hH} L ${o} ${o + rx} Q ${o} ${o} ${o + rx} ${o} Z`;
    },
    nomeFontSize(serra) {
      const w = serra.larghezza || 10;
      return Math.min(1.4, Math.max(0.65, w / 8));
    },

    /* ---- posizione/rotazione serre ---- */
    posCX(pos) {
      return this.temp_pos[pos.id] !== undefined ? this.temp_pos[pos.id].cx : (pos.cx ?? pos.x);
    },
    posCY(pos) {
      return this.temp_pos[pos.id] !== undefined ? this.temp_pos[pos.id].cy : (pos.cy ?? pos.y);
    },
    posX(pos) { return this.posCX(pos) - (pos.serra.larghezza || 10) / 2; },
    posY(pos) { return this.posCY(pos) - (pos.serra.lunghezza  || 20) / 2; },
    posRot(pos) {
      return this.temp_rot[pos.id] !== undefined ? this.temp_rot[pos.id] : (pos.rotazione || 0);
    },
    serraTransform(pos) {
      const x   = this.posX(pos);
      const y   = this.posY(pos);
      const rot = this.posRot(pos);
      const w   = pos.serra.larghezza || 10;
      const h   = pos.serra.lunghezza || 20;
      if (!rot) return `translate(${x}, ${y})`;
      return `translate(${x}, ${y}) rotate(${rot}, ${w / 2}, ${h / 2})`;
    },

    /* ---- posizione/rotazione blocchi ---- */
    posCXBlocco(b) {
      return this.temp_pos_b[b.id] !== undefined ? this.temp_pos_b[b.id].cx : (b.cx ?? b.x ?? 0);
    },
    posCYBlocco(b) {
      return this.temp_pos_b[b.id] !== undefined ? this.temp_pos_b[b.id].cy : (b.cy ?? b.y ?? 0);
    },
    posRotBlocco(b) {
      return this.temp_rot_b[b.id] !== undefined ? this.temp_rot_b[b.id] : (b.rotazione || 0);
    },
    bloccoTransform(b) {
      const w   = b.larghezza || 10;
      const h   = b.lunghezza || 5;
      const x   = this.posCXBlocco(b) - w / 2;
      const y   = this.posCYBlocco(b) - h / 2;
      const rot = this.posRotBlocco(b);
      if (!rot) return `translate(${x}, ${y})`;
      return `translate(${x}, ${y}) rotate(${rot}, ${w / 2}, ${h / 2})`;
    },

    /* ---- coordinate screen → SVG ---- */
    screenToSvg(event) {
      const svg = this.$refs.svg_canvas;
      if (!svg) return { x: 0, y: 0 };
      const pt  = svg.createSVGPoint();
      pt.x = event.clientX;
      pt.y = event.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      return pt.matrixTransform(ctm.inverse());
    },

    /* ---- drag serre ---- */
    startDrag(event, pos) {
      event.preventDefault();
      if (!this.isEditMode) {
        // lettura: registra solo id per il click in onMouseUp
        this.dragging   = { _tipo: 'serra', id: pos.id, id_serra: pos.id_serra, origin_cx: null, origin_cy: null };
        this.drag_start = { cx: event.clientX, cy: event.clientY };
        return;
      }
      const pt  = this.screenToSvg(event);
      const ocx = this.posCX(pos);
      const ocy = this.posCY(pos);
      this.dragging = {
        _tipo: 'serra', id: pos.id, id_serra: pos.id_serra,
        offset_cx: pt.x - ocx, offset_cy: pt.y - ocy,
        rot: this.posRot(pos), origin_cx: ocx, origin_cy: ocy,
        _w: pos.serra.larghezza || 10, _h: pos.serra.lunghezza || 20,
      };
      this.drag_start = { cx: event.clientX, cy: event.clientY };
    },

    /* ---- drag blocchi ---- */
    startDragBlocco(event, blocco) {
      event.preventDefault();
      if (!this.isEditMode) return;
      const pt  = this.screenToSvg(event);
      const ocx = this.posCXBlocco(blocco);
      const ocy = this.posCYBlocco(blocco);
      this.dragging = {
        _tipo: 'blocco', id: blocco.id,
        offset_cx: pt.x - ocx, offset_cy: pt.y - ocy,
        rot: this.posRotBlocco(blocco), origin_cx: ocx, origin_cy: ocy,
        _w: blocco.larghezza || 10, _h: blocco.lunghezza || 5,
      };
      this.drag_start = { cx: event.clientX, cy: event.clientY };
    },

    /* ---- rotazione serre ---- */
    startRotate(event, pos) {
      event.preventDefault();
      const pt = this.screenToSvg(event);
      const cx = this.posCX(pos);
      const cy = this.posCY(pos);
      this.rotating = {
        _tipo: 'serra', id: pos.id, id_serra: pos.id_serra, cx, cy,
        start_angle: Math.atan2(pt.y - cy, pt.x - cx) * 180 / Math.PI,
        start_rot: this.posRot(pos),
      };
      this.drag_start = { cx: event.clientX, cy: event.clientY };
    },

    /* ---- rotazione blocchi ---- */
    startRotateBlocco(event, blocco) {
      event.preventDefault();
      const pt = this.screenToSvg(event);
      const cx = this.posCXBlocco(blocco);
      const cy = this.posCYBlocco(blocco);
      this.rotating = {
        _tipo: 'blocco', id: blocco.id, cx, cy,
        start_angle: Math.atan2(pt.y - cy, pt.x - cx) * 180 / Math.PI,
        start_rot: this.posRotBlocco(blocco),
      };
      this.drag_start = { cx: event.clientX, cy: event.clientY };
    },

    /* ---- mouse move (unificato) ---- */
    onMouseMove(event) {
      const svg = this.$refs.svg_canvas;
      if (svg && !this.dragging && !this.rotating) {
        const rect = svg.getBoundingClientRect();
        this.hover_mouse = { x: event.clientX - rect.left + 16, y: event.clientY - rect.top + 16 };
      }
      if (this.panning) {
        const svg_el = this.$refs.svg_canvas;
        if (svg_el) {
          const rect     = svg_el.getBoundingClientRect();
          const px_per_m = rect.width / (this.W / this.zoom_scale);
          const dx       = (event.clientX - this.panning.start_cx) / px_per_m;
          const dy       = (event.clientY - this.panning.start_cy) / px_per_m;
          this.pan_cx    = this.panning.origin_px - dx;
          this.pan_cy    = this.panning.origin_py - dy;
        }
        return;
      }
      if (this.rotating) {
        const pt      = this.screenToSvg(event);
        const angle   = Math.atan2(pt.y - this.rotating.cy, pt.x - this.rotating.cx) * 180 / Math.PI;
        const new_rot = Math.round((((this.rotating.start_rot + angle - this.rotating.start_angle) % 360) + 360) % 360 / 5) * 5;
        const dict    = this.rotating._tipo === 'blocco' ? 'temp_rot_b' : 'temp_rot';
        this.$set(this[dict], this.rotating.id, new_rot);
        return;
      }
      if (this.dragging && this.dragging.offset_cx !== undefined) {
        const pt      = this.screenToSvg(event);
        const sw      = this.dragging._w;
        const sh      = this.dragging._h;
        let cx = Math.round((pt.x - this.dragging.offset_cx) * 2) / 2;
        let cy = Math.round((pt.y - this.dragging.offset_cy) * 2) / 2;
        const rot_rad = (this.dragging.rot || 0) * Math.PI / 180;
        const aabb_w  = Math.abs(sw * Math.cos(rot_rad)) + Math.abs(sh * Math.sin(rot_rad));
        const aabb_h  = Math.abs(sw * Math.sin(rot_rad)) + Math.abs(sh * Math.cos(rot_rad));
        cx = Math.max(aabb_w / 2, Math.min(cx, this.W - aabb_w / 2));
        cy = Math.max(aabb_h / 2, Math.min(cy, this.H - aabb_h / 2));
        const dict = this.dragging._tipo === 'blocco' ? 'temp_pos_b' : 'temp_pos';
        this.$set(this[dict], this.dragging.id, { cx, cy });
      }
    },

    /* ---- mouse up (unificato) ---- */
    onMouseUp() {
      if (this.panning) { this.panning = null; return; }
      if (this.rotating) {
        this._salvaRotazione();
        this.rotating   = null;
        this.drag_start = null;
        return;
      }
      if (this.dragging && this.drag_start) {
        if (!this.isEditMode) {
          // lettura: click apre la serra (solo se non dismessa in live), i blocchi non fanno nulla
          if (this.dragging._tipo === 'serra') {
            const pos = this.posizioni_con_serra.find(p => p.id_serra === this.dragging.id_serra);
            const dismessa_live = !this.fork_mode && pos?.serra?.dismessa;
            if (!dismessa_live) this.$emit('serra-selezionata', this.dragging.id_serra);
          }
          this.dragging   = null;
          this.drag_start = null;
          return;
        }
        const pos_dict = this.dragging._tipo === 'blocco' ? 'temp_pos_b' : 'temp_pos';
        const tmp      = this[pos_dict][this.dragging.id];
        const moved    = tmp && (tmp.cx !== this.dragging.origin_cx || tmp.cy !== this.dragging.origin_cy);
        if (!moved) {
          if (this.dragging._tipo === 'blocco') {
            this.apriEditBlocco(this.dragging.id);
          } else {
            this.$emit('modifica-serra', this.dragging.id_serra);
          }
        } else {
          this._salvaTraslazione(tmp);
        }
        this.dragging   = null;
        this.drag_start = null;
      }
    },

    /* ---- mouse leave (unificato) ---- */
    onMouseLeave() {
      this.hover_id_serra = null;
      if (this.panning) { this.panning = null; return; }
      if (this.rotating) {
        this._salvaRotazione();
        this.rotating   = null;
        this.drag_start = null;
        return;
      }
      if (this.dragging && this.isEditMode) {
        const pos_dict = this.dragging._tipo === 'blocco' ? 'temp_pos_b' : 'temp_pos';
        const tmp      = this[pos_dict][this.dragging.id];
        if (tmp) this._salvaTraslazione(tmp);
      }
      this.dragging   = null;
      this.drag_start = null;
    },

    /* ---- helper salvataggio (evita duplicazione onMouseUp/onMouseLeave) ---- */
    _salvaRotazione() {
      const isBlocco = this.rotating._tipo === 'blocco';
      const rot_dict = isBlocco ? 'temp_rot_b' : 'temp_rot';
      const tmp_rot  = this[rot_dict][this.rotating.id];
      if (tmp_rot === undefined) return;
      if (isBlocco) {
        const b = this.blocchi.find((bl) => bl.id === this.rotating.id);
        if (b) this.$emit('blocco-aggiornato', { id: b.id, cx: this.posCXBlocco(b), cy: this.posCYBlocco(b), rotazione: tmp_rot });
      } else {
        const pos = this.posizioni_con_serra.find((p) => p.id === this.rotating.id);
        if (pos) this.$emit('posizione-aggiornata', { id: pos.id, cx: this.posCX(pos), cy: this.posCY(pos), rotazione: tmp_rot });
      }
    },
    _salvaTraslazione(tmp) {
      if (this.dragging._tipo === 'blocco') {
        const b = this.blocchi.find((bl) => bl.id === this.dragging.id);
        if (b) this.$emit('blocco-aggiornato', { id: b.id, cx: tmp.cx, cy: tmp.cy, rotazione: this.posRotBlocco(b) });
      } else {
        const pos = this.posizioni_con_serra.find((p) => p.id === this.dragging.id);
        if (pos) this.$emit('posizione-aggiornata', { id: pos.id, cx: tmp.cx, cy: tmp.cy, rotazione: this.posRot(pos) });
      }
    },

    /* ---- posizionamento ---- */
    posizionaCentro(serra) {
      const cx = Math.round(this.W / 2 * 2) / 2;
      const cy = Math.round(this.H / 2 * 2) / 2;
      this.$emit('posizione-aggiunta', { id_serra: serra.id, cx, cy, rotazione: 0 });
    },
    aggiungiBlocco() {
      const cx = Math.round(this.W / 2 * 2) / 2;
      const cy = Math.round(this.H / 2 * 2) / 2;
      this.$emit('blocco-aggiunto', { cx, cy, larghezza: 10, lunghezza: 5, rotazione: 0, label: '', colore: '#CFD8DC' });
    },

    /* ---- dialog modifica blocco ---- */
    apriEditBlocco(id_blocco) {
      const b = this.blocchi.find((bl) => bl.id === id_blocco);
      if (!b) return;
      this.blocco_editing_id = id_blocco;
      this.blocco_editing    = { label: b.label || '', larghezza: b.larghezza || 10, lunghezza: b.lunghezza || 5, colore: b.colore || '#CFD8DC' };
      this.dialog_blocco     = true;
    },
    salvaBlocco() {
      const b = this.blocchi.find((bl) => bl.id === this.blocco_editing_id);
      if (!b) return;
      this.$emit('blocco-aggiornato', {
        id:        b.id,
        cx:        this.posCXBlocco(b),
        cy:        this.posCYBlocco(b),
        rotazione: this.posRotBlocco(b),
        ...this.blocco_editing,
      });
      this.dialog_blocco = false;
    },
  },
};
</script>

<style scoped>
.zoom-overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  font-size: 11px;
  border-radius: 4px;
  padding: 2px 8px;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}
.zoom-reset {
  cursor: pointer;
  opacity: 0.75;
  font-size: 10px;
}
.zoom-reset:hover {
  opacity: 1;
}
.coords-overlay {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 11px;
  border-radius: 4px;
  padding: 2px 8px;
  pointer-events: none;
}
.serra-tooltip {
  position: absolute;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.13);
  z-index: 20;
  pointer-events: none;
  min-width: 260px;
  max-width: 380px;
}
.tooltip-row {
  padding: 2px 0;
}
.tooltip-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
}
.tooltip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}
.tooltip-footer {
  border-top: 1px solid #f0f0f0;
  padding-top: 4px;
}
</style>
